import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Layout from "../../components/Layout";
import Card from "../../components/ui/Card";
import { paymentTransactionsService } from "../../services/payment-transactions.service";
import {
  PaymentTransaction,
  PageMeta,
  ListPaymentsParams,
} from "../../interfaces/payment-transaction.interface";

const TYPE_OPTIONS = [
  { value: "", label: "all_types" },
  { value: "POINTS_TOPUP",         label: "type_points_topup" },
  { value: "BOOKING_INSTANT",      label: "type_booking_instant" },
  { value: "BOOKING_PAYMENT_LINK", label: "type_booking_payment_link" },
];

const STATUS_OPTIONS = [
  { value: "",          label: "all_statuses" },
  { value: "succeeded", label: "status_succeeded" },
  { value: "failed",    label: "status_failed" },
];

const TYPE_BADGE: Record<string, string> = {
  POINTS_TOPUP:         "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
  BOOKING_INSTANT:      "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300",
  BOOKING_PAYMENT_LINK: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300",
};

const STATUS_BADGE: Record<string, string> = {
  succeeded: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
  failed:    "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
};

const selectClass =
  "px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500";
const inputClass =
  "px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500";

const DEFAULT_TAKE = 20;

const PaymentTransactionsPage = () => {
  const { t } = useTranslation("common");

  const [rows, setRows]       = useState<PaymentTransaction[]>([]);
  const [meta, setMeta]       = useState<PageMeta | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState("");

  const [filters, setFilters] = useState<ListPaymentsParams>({
    type: "", status: "", userId: "", from: "", to: "",
  });
  const [page, setPage] = useState(1);
  const [copied, setCopied] = useState<string | null>(null);

  const setF = (patch: Partial<ListPaymentsParams>) =>
    setFilters((f) => ({ ...f, ...patch }));

  const load = useCallback(async (p: number, f: ListPaymentsParams) => {
    setLoading(true);
    setError("");
    try {
      const params: ListPaymentsParams = {
        page: p,
        take: DEFAULT_TAKE,
        ...(f.type   ? { type:   f.type }   : {}),
        ...(f.status ? { status: f.status } : {}),
        ...(f.userId ? { userId: f.userId } : {}),
        ...(f.from   ? { from:   f.from }   : {}),
        ...(f.to     ? { to:     f.to }     : {}),
      };
      const res = await paymentTransactionsService.list(params);
      setRows(res.data);
      setMeta(res.meta);
    } catch {
      setError(t("load_error"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(page, filters); }, [page]);

  const handleSearch = () => {
    setPage(1);
    load(1, filters);
  };

  const handleReset = () => {
    const empty: ListPaymentsParams = { type: "", status: "", userId: "", from: "", to: "" };
    setFilters(empty);
    setPage(1);
    load(1, empty);
  };

  const copyStripeId = (id: string) => {
    navigator.clipboard.writeText(id).then(() => {
      setCopied(id);
      setTimeout(() => setCopied(null), 1500);
    });
  };

  return (
    <Layout>
      <div className="space-y-6">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
          {t("payment_transactions")}
        </h1>

        {/* Filters */}
        <Card>
          <div className="flex flex-wrap gap-3 items-end p-4">
            {/* Type */}
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                {t("type")}
              </label>
              <select
                className={selectClass}
                value={filters.type ?? ""}
                onChange={(e) => setF({ type: e.target.value })}
              >
                {TYPE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{t(o.label)}</option>
                ))}
              </select>
            </div>

            {/* Status */}
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                {t("status")}
              </label>
              <select
                className={selectClass}
                value={filters.status ?? ""}
                onChange={(e) => setF({ status: e.target.value })}
              >
                {STATUS_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{t(o.label)}</option>
                ))}
              </select>
            </div>

            {/* User ID */}
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                {t("user_id")}
              </label>
              <input
                className={inputClass}
                placeholder="UUID"
                value={filters.userId ?? ""}
                onChange={(e) => setF({ userId: e.target.value })}
              />
            </div>

            {/* From */}
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                {t("from_date")}
              </label>
              <input
                type="date"
                className={inputClass}
                value={filters.from ?? ""}
                onChange={(e) => setF({ from: e.target.value })}
              />
            </div>

            {/* To */}
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                {t("to_date")}
              </label>
              <input
                type="date"
                className={inputClass}
                value={filters.to ?? ""}
                onChange={(e) => setF({ to: e.target.value })}
              />
            </div>

            <button
              onClick={handleSearch}
              className="px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              {t("search")}
            </button>
            <button
              onClick={handleReset}
              className="px-4 py-2 text-sm font-medium border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              {t("reset")}
            </button>
          </div>
        </Card>

        {/* Table */}
        <Card>
          {loading && (
            <div className="p-8 text-center text-gray-400 text-sm">{t("loading")}</div>
          )}
          {error && (
            <div className="p-4 text-red-600 text-sm">{error}</div>
          )}
          {!loading && !error && (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead>
                    <tr className="bg-gray-50 dark:bg-gray-800">
                      {["date", "user", "type", "amount", "stripe_id", "status"].map((h) => (
                        <th
                          key={h}
                          className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400"
                        >
                          {t(h)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-100 dark:divide-gray-800">
                    {rows.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="px-4 py-8 text-center text-gray-400 text-sm">
                          {t("no_records")}
                        </td>
                      </tr>
                    ) : (
                      rows.map((row) => (
                        <tr key={row.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                          {/* Date */}
                          <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap">
                            {new Date(row.createdAt).toLocaleString()}
                          </td>
                          {/* User */}
                          <td className="px-4 py-3 text-sm">
                            <div className="font-medium text-gray-900 dark:text-white">{row.userFullName}</div>
                            <div className="text-xs text-gray-400">{row.userEmail}</div>
                          </td>
                          {/* Type */}
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${TYPE_BADGE[row.type] ?? ""}`}>
                              {t(`type_${row.type.toLowerCase()}`)}
                            </span>
                          </td>
                          {/* Amount */}
                          <td className="px-4 py-3 text-sm font-mono text-gray-900 dark:text-white whitespace-nowrap">
                            {row.amount} {row.currency}
                          </td>
                          {/* Stripe ID */}
                          <td className="px-4 py-3">
                            {row.stripeId ? (
                              <button
                                onClick={() => copyStripeId(row.stripeId!)}
                                title={copied === row.stripeId ? t("copied") : row.stripeId}
                                className="font-mono text-xs text-blue-600 dark:text-blue-400 hover:underline truncate max-w-[160px] block"
                              >
                                {copied === row.stripeId ? t("copied") : row.stripeId.slice(0, 18) + "…"}
                              </button>
                            ) : (
                              <span className="text-gray-400 text-xs">—</span>
                            )}
                          </td>
                          {/* Status */}
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${STATUS_BADGE[row.status] ?? ""}`}>
                              {t(`status_${row.status}`)}
                            </span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {meta && meta.pageCount > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-gray-700">
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {t("page_of", { page: meta.page, total: meta.pageCount })}
                    {" "}({meta.total} {t("records")})
                  </p>
                  <div className="flex gap-2">
                    <button
                      disabled={page <= 1}
                      onClick={() => setPage((p) => p - 1)}
                      className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      {t("previous")}
                    </button>
                    <button
                      disabled={!meta || page >= meta.pageCount}
                      onClick={() => setPage((p) => p + 1)}
                      className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      {t("next")}
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </Card>
      </div>
    </Layout>
  );
};

export default PaymentTransactionsPage;
